// Vendor Libs
import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {get} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';

// Components
import Button from '../core/button';
import Error from '../core/error';
import Loading from '../core/loading';
import MarkersCheckbox from './markers-checkbox';
import Pagination from '../core/pagination';
import PanelAction from '../core/panel-action';

// Actions
import {fetchMarkerFingerprint, resetMarkerFingerprint} from '../../actions/marker-fingerprint-actions';
import {generateMarker, generateMarkerReset} from '../../actions/marker-generation-actions';

// Utilities
import formatMarkerTime from '../../lib/app/format-marker-time';

// Containers
import withSearch from '../../containers/with-search';

// Contexts
import {SearchContext} from '../../contexts/search-context';

// Styles
import './markers-tab.scss';

const servicesDataKey = 'mediaItemDetail';

const STATUS_COLORS = {
    PENDING: 'orange',
    COMPLETE: 'green',
    FAILED: 'red',
    UNKNOWN: 'red',
    INSUFFICIENT_DATA: 'amber',
};

const getStatusColor = (status) => {
    if (STATUS_COLORS[status]) return STATUS_COLORS[status];
    if (status && status.includes('INSUFFICIENT')) return 'amber';
    return 'red';
};

function MarkersTab({fetching, routerParams, searchData}) {
    const dispatch = useDispatch();
    const {refreshServicesData} = useContext(SearchContext);
    const mediaItem = get(searchData, '[0]', null);
    const markerFingerprint = useSelector((state) => state.markerFingerprint);
    const markerGeneration = useSelector((state) => state.markerGeneration);

    const {guid} = routerParams;

    // Local state for generation options
    const [forceRegenerate, setForceRegenerate] = useState(false);
    const [forceIncludeFirstEpisode, setForceIncludeFirstEpisode] = useState(false);
    const [selectedOfferingIndex, setSelectedOfferingIndex] = useState(0);

    // Derive fingerprint lookup IDs from media item
    const echostarId = get(mediaItem, 'amsFranchise.echostarId');
    const seriesId = get(mediaItem, 'amsFranchise.seriesId');
    const seasonNumber = get(mediaItem, 'amsEpisode.episodeSeason') ?? get(mediaItem, 'seasonNumber');
    const networkId = get(mediaItem, 'externalNetwork.network.networkId');

    // Reset state on media item change
    useEffect(() => {
        dispatch(resetMarkerFingerprint());
        dispatch(generateMarkerReset());
        setForceRegenerate(false);
        setForceIncludeFirstEpisode(false);
        setSelectedOfferingIndex(0);
    }, [guid]);

    // Fetch fingerprint when the loaded media item matches the current guid and all IDs are available
    useEffect(() => {
        const mediaItemGuid = get(mediaItem, 'guid');
        if (mediaItemGuid === guid && echostarId && seriesId && seasonNumber != null && networkId) {
            dispatch(fetchMarkerFingerprint({echostarId, seriesId, seasonNumber, networkId}));
        }
    }, [guid, mediaItem, echostarId, seriesId, seasonNumber, networkId]);

    // Gating precedence: (a) → (b) → (c) → (d) → (e) → render content
    if (!guid) {
        return <Error error="Choose a record." />;
    }

    if ((fetching && !mediaItem) || (mediaItem && mediaItem.guid !== guid)) {
        return <Loading loading={true} />;
    }

    if (!mediaItem) {
        return <Error error="Record not found." />;
    }

    if (!mediaItem.amsEpisode) {
        return <Error error="Markers are only available for episodic content." />;
    }

    if (!get(mediaItem, 'externalNetwork.network.bingeable')) {
        return <Error error="Markers are unavailable for non-bingeable networks." />;
    }

    // Get offerings and selected offering's segments
    const offerings = get(mediaItem, 'offerings', []);
    const currentOffering = get(offerings, `[${selectedOfferingIndex}]`, null);
    const currentSegments = get(currentOffering, 'segments', []);
    const introSegments = currentSegments.filter((s) => s.segmentType === 'INTRO');
    const endCreditsSegments = currentSegments.filter((s) => s.segmentType === 'END_CREDITS');
    const postCreditsSegments = currentSegments.filter((s) => s.segmentType === 'POST_CREDITS');

    const renderFingerprintSection = () => {
        if (markerFingerprint.fetching) {
            return <Loading loading={true} small={true} />;
        }

        if (!echostarId || !seriesId || seasonNumber == null || !networkId) {
            return <span className="markers-tab--na">Not generated</span>;
        }

        if (markerFingerprint.error || !markerFingerprint.data) {
            return <span className="markers-tab--na">Not generated</span>;
        }

        const {fingerprintVersion, status, dateCreated, lastUpdated, episodeNumbers} = markerFingerprint.data;
        const statusColor = getStatusColor(status);
        const isInsufficient = status && status.includes('INSUFFICIENT');
        const statusTooltip = isInsufficient
            ? (episodeNumbers && episodeNumbers.length > 0
                ? `Insufficient data: A minimum of 3 episodes is required, but only ${episodeNumbers.length} ${episodeNumbers.length === 1 ? 'episode is' : 'episodes are'} available (${episodeNumbers.join(', ')}).`
                : 'Insufficient data: A minimum of 3 episodes is required, but none are available.')
            : '';

        return (
            <div className="markers-tab--row">
                <span className="markers-tab--title">Version:</span>
                <span className="markers-tab--desc">{fingerprintVersion || '—'}</span>
                <span className="markers-tab--title">Status:</span>
                <span className={`markers-tab--desc markers-tab--status-${statusColor}`} title={statusTooltip}>
                    {status || '—'}
                </span>
                <span className="markers-tab--title">Episodes:</span>
                <span className="markers-tab--desc">
                    {episodeNumbers && episodeNumbers.length > 0 ? episodeNumbers.join(', ') : '—'}
                </span>
                <span className="markers-tab--title">Created:</span>
                <span className="markers-tab--desc">{dateCreated || '—'}</span>
                <span className="markers-tab--title">Updated:</span>
                <span className="markers-tab--desc">{lastUpdated || '—'}</span>
            </div>
        );
    };

    const renderSegmentSection = (segments) => {
        if (!segments || segments.length === 0) {
            return <span className="markers-tab--na">Not generated</span>;
        }

        return segments.map((segment, index) => (
            <div key={segment.segmentId || index} className="markers-tab--row">
                <span className="markers-tab--title">Start:</span>
                <span className="markers-tab--desc">{formatMarkerTime(segment.startTime)}</span>
                <span className="markers-tab--title">End:</span>
                <span className="markers-tab--desc">{formatMarkerTime(segment.endTime)}</span>
                <span className="markers-tab--title">Confidence:</span>
                <span className="markers-tab--desc">
                    {segment.confidence != null ? `${segment.confidence}%` : '—'}
                </span>
            </div>
        ));
    };

    const handleGenerate = (markerType) => {
        dispatch(generateMarker({
            guid,
            markerType,
            forceRegenerate,
            forceIncludeFirstEpisode,
        })).then(() => {
            setForceRegenerate(false);
            setForceIncludeFirstEpisode(false);
        });
    };

    const handleRefresh = () => {
        refreshServicesData();
        if (echostarId && seriesId && seasonNumber != null && networkId) {
            dispatch(fetchMarkerFingerprint({echostarId, seriesId, seasonNumber, networkId}));
        }
    };

    const renderGenerationControls = () => {
        const {requests, lastFulfilledType} = markerGeneration;
        const fingerprintComplete = get(markerFingerprint, 'data.status') === 'COMPLETE';
        const introDisabled = requests.INTRO.fetching || !fingerprintComplete;

        return (
            <div className="markers-tab--section">
                <span className="markers-tab--section-label">Generate Markers</span>
                <div className="markers-tab--options">
                    <MarkersCheckbox
                        checked={forceRegenerate}
                        label="Force Regenerate"
                        onChange={setForceRegenerate}
                    />
                    <MarkersCheckbox
                        checked={forceIncludeFirstEpisode}
                        label="Force Include First Episode"
                        onChange={setForceIncludeFirstEpisode}
                    />
                </div>
                <div className="markers-tab--buttons">
                    <Button
                        className="markers-tab--generate-button"
                        disabled={requests.FINGERPRINT.fetching}
                        onClick={() => handleGenerate('FINGERPRINT')}
                        title="Generate fingerprint for this asset"
                    >
                        {requests.FINGERPRINT.fetching ? 'Generating...' : 'Generate Fingerprint'}
                    </Button>
                    <span title={!fingerprintComplete ? 'Fingerprint must be generated first (status: COMPLETE required)' : ''}>
                        <Button
                            className="markers-tab--generate-button"
                            disabled={introDisabled}
                            onClick={() => handleGenerate('INTRO')}
                        >
                            {requests.INTRO.fetching ? 'Generating...' : 'Generate Intro'}
                        </Button>
                    </span>
                    <Button
                        className="markers-tab--generate-button"
                        disabled={requests.CREDITS.fetching}
                        onClick={() => handleGenerate('CREDITS')}
                        title="Generate credits markers for this asset"
                    >
                        {requests.CREDITS.fetching ? 'Generating...' : 'Generate Credits'}
                    </Button>
                </div>
                {lastFulfilledType && (
                    <div className="markers-tab--response">
                        Marker generation request queued for {lastFulfilledType}.
                    </div>
                )}
                {requests.FINGERPRINT.error && <Error error={requests.FINGERPRINT.error} />}
                {requests.INTRO.error && <Error error={requests.INTRO.error} />}
                {requests.CREDITS.error && <Error error={requests.CREDITS.error} />}
            </div>
        );
    };

    return (
        <div className="markers-tab">
            <Loading loading={fetching} />
            <PanelAction
                canTogglePanel={false}
                icon="refresh"
                onClick={handleRefresh}
                title="Refresh Markers"
                weight={-20}
            />
            <div className="markers-tab--section">
                <span className="markers-tab--section-label">Fingerprint</span>
                {renderFingerprintSection()}
            </div>
            {offerings.length > 1 && (
                <div className="markers-tab--offering-nav">
                    <Pagination
                        className="markers-tab--pagination"
                        currentPage={selectedOfferingIndex + 1}
                        onClick={(page) => setSelectedOfferingIndex(page - 1)}
                        totalPages={offerings.length}
                    >
                        Offerings:
                    </Pagination>
                </div>
            )}
            {currentOffering && (
                <div className="markers-tab--ruid">
                    <span className="markers-tab--title">RUID:</span>
                    <span className="markers-tab--desc">{currentOffering.resourceId || '—'}</span>
                </div>
            )}
            <div className="markers-tab--section">
                <span className="markers-tab--section-label">Intro</span>
                {renderSegmentSection(introSegments)}
            </div>
            <div className="markers-tab--section">
                <span className="markers-tab--section-label">End Credits</span>
                {renderSegmentSection(endCreditsSegments)}
            </div>
            {postCreditsSegments.length > 0 && (
                <div className="markers-tab--section">
                    <span className="markers-tab--section-label">Post Credits</span>
                    {renderSegmentSection(postCreditsSegments)}
                </div>
            )}
            {renderGenerationControls()}
        </div>
    );
}

MarkersTab.propTypes = {
    fetching: PropTypes.bool,
    routerParams: PropTypes.object.isRequired,
    searchData: PropTypes.array,
};

export default withSearch(MarkersTab, servicesDataKey);
